import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react'
import SearchInput from './SearchInput'
import useInlineFloatingMenu from '../../hooks/useInlineFloatingMenu'

const POPOVER_BASE_CLASS = 'tw:z-[1100] tw:bg-white tw:rounded-md! tw:p-2 '
  + 'tw:shadow-ds-popover!'
const LIST_CLASS = 'tw:mt-2 tw:max-h-64 tw:overflow-y-auto tw:overflow-x-hidden '
  + 'tw:flex tw:flex-col tw:gap-2'
const EMPTY_CLASS = 'tw:py-2 tw:text-sm tw:text-gray-500 tw:text-center'

export const SEARCHABLE_MENU_ROW_CLASS = 'tw:w-full tw:flex tw:items-center tw:gap-2 '
  + 'tw:py-2 tw:px-3 tw:rounded! tw:bg-transparent tw:border-0 tw:cursor-pointer '
  + 'tw:text-left tw:hover:bg-gray-100 tw:focus-visible:bg-gray-100'

const normalize = value => value.toLocaleLowerCase('fr-FR')

const SearchableMenu = ({
  anchorEl = null, ariaLabel, header = null, isOpen, items,
  filterBy, onClose, renderItem, searchable = true,
  searchName, searchPlaceholder = 'Rechercher...', widthClass = 'tw:w-64'
}) => {
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (isOpen)
      setSearchTerm('')
  }, [isOpen])

  const { refs, floatingStyles, context, getFloatingProps } = useInlineFloatingMenu({
    anchorEl, isOpen, onClose
  })

  const handleSearchChange = useCallback(event => setSearchTerm(event.target.value), [])

  const filteredItems = useMemo(() => {
    if (!searchable || !searchTerm.trim())
      return items
    const needle = normalize(searchTerm.trim())
    return items.filter(item => normalize(filterBy(item)).includes(needle))
  }, [items, searchable, searchTerm, filterBy])

  if (!isOpen || !anchorEl)
    return null

  const renderList = () => {
    if (filteredItems.length === 0)
      return <p className={EMPTY_CLASS}>Aucun résultat</p>
    return filteredItems.map(renderItem)
  }

  return (
    <FloatingPortal>
      <FloatingFocusManager context={context} initialFocus={-1} modal={false}>
        <div
          ref={refs.setFloating}
          aria-label={ariaLabel}
          className={`${POPOVER_BASE_CLASS} ${widthClass}`}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          {searchable ? (
            <SearchInput
              name={searchName}
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
            />
          ) : null}
          {header ? <div className="tw:mt-2">{header}</div> : null}
          <div className={LIST_CLASS}>
            {renderList()}
          </div>
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  )
}

SearchableMenu.propTypes = {
  anchorEl: PropTypes.instanceOf(Element),
  ariaLabel: PropTypes.string.isRequired,
  filterBy: PropTypes.func,
  header: PropTypes.node,
  isOpen: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onClose: PropTypes.func.isRequired,
  renderItem: PropTypes.func.isRequired,
  searchName: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  searchable: PropTypes.bool,
  widthClass: PropTypes.string
}

SearchableMenu.defaultProps = {
  anchorEl: null,
  filterBy: null,
  header: null,
  searchName: null,
  searchPlaceholder: 'Rechercher...',
  searchable: true,
  widthClass: 'tw:w-64'
}

export default SearchableMenu
